import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import ConcludeAnimation from '@/components/animations/ConcludeAnimation';
import {
    type IconComponent,
    ArrowRight,
    TickCircle,
    ArrowDown2,
    ArrowUp2,
    ClipboardTick,
    TaskSquare,
    DocumentText,
    Profile,
    People,
} from '@/components/icons';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useLocalStorage } from '@/hooks/use-local-storage';
import { useIsMobile } from '@/hooks/use-mobile';
import { useReadinessChecklist } from '@/hooks/use-readiness-checklist';
import { capture } from '@/lib/posthog';
import { cn } from '@/lib/utils';
import { DashboardPage, PatientsPage, ProfilePage, ReportPage } from '@/pages/constants';
import { createPageUrl } from '@/utils';
import type { ReadinessStepKey } from '@/api/readiness';

type StepConfig = {
    title: string;
    description: string;
    actionLabel?: string;
    actionPath?: string;
    icon: IconComponent;
};

const STEP_CONFIG: Record<ReadinessStepKey, StepConfig> = {
    account_created: {
        title: 'Conta criada',
        description: 'Seu acesso ao LaudoPsi está ativo.',
        icon: ClipboardTick,
    },
    profile_completed: {
        title: 'Complete seu perfil profissional',
        description: 'Informe nome e CRP para evitar bloqueios no fechamento do laudo.',
        actionLabel: 'Atualizar perfil',
        actionPath: createPageUrl(ProfilePage),
        icon: Profile,
    },
    first_patient_added: {
        title: 'Adicione seu primeiro paciente',
        description: 'Crie o registro que vai concentrar laudos, instrumentos e formulários.',
        actionLabel: 'Adicionar paciente',
        actionPath: createPageUrl(PatientsPage),
        icon: People,
    },
    first_report_created: {
        title: 'Crie seu primeiro laudo',
        description: 'Comece pelo paciente e deixe o editor salvar seu progresso.',
        actionLabel: 'Criar laudo',
        actionPath: createPageUrl(ReportPage),
        icon: DocumentText,
    },
    first_report_completed: {
        title: 'Conclua seu primeiro laudo',
        description: 'Finalize o documento para chegar ao primeiro resultado real.',
        actionLabel: 'Ver laudos',
        actionPath: createPageUrl(DashboardPage),
        icon: TaskSquare,
    },
};

const buildStepMap = (steps: Array<{ key: ReadinessStepKey; completed: boolean }>) => {
    return steps.reduce<Record<ReadinessStepKey, boolean>>(
        (accumulator, step) => ({
            ...accumulator,
            [step.key]: step.completed,
        }),
        {} as Record<ReadinessStepKey, boolean>,
    );
};

export default function ReadinessChecklist() {
    const { checklist, isError, isLoading, markCompleted } = useReadinessChecklist();
    const isMobile = useIsMobile();
    // `null` means "never chosen", which is what lets the default depend on the
    // viewport: expanded it is `inset-x-4` and ~580px tall, so on a phone it
    // covers the page and swallows taps meant for the content underneath.
    // An explicit choice still wins in both directions.
    const [storedCollapsed, setStoredCollapsed] = useLocalStorage<boolean | null>(
        'readiness_checklist_collapsed',
        null,
    );
    const isCollapsed = storedCollapsed ?? isMobile;
    const setIsCollapsed = (update: (current: boolean) => boolean) =>
        setStoredCollapsed(update(isCollapsed));
    const [showConfetti, setShowConfetti] = useState(false);
    const [celebratedStepKey, setCelebratedStepKey] = useState<ReadinessStepKey | null>(null);
    const previousStepsRef = useRef<Record<ReadinessStepKey, boolean> | null>(null);
    const confettiTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const completionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const hasRequestedCompletionRef = useRef(false);

    useEffect(() => {
        if (!checklist || checklist.hidden) return;

        const currentSteps = buildStepMap(checklist.steps);
        const previousSteps = previousStepsRef.current;

        if (!previousSteps) {
            previousStepsRef.current = currentSteps;
            return;
        }

        const newlyCompletedStep = [...checklist.steps]
            .reverse()
            .find((step) => step.completed && previousSteps[step.key] === false);

        previousStepsRef.current = currentSteps;

        if (!newlyCompletedStep) return;

        setCelebratedStepKey(newlyCompletedStep.key);
        setShowConfetti(true);

        if (confettiTimeoutRef.current) {
            clearTimeout(confettiTimeoutRef.current);
        }

        confettiTimeoutRef.current = setTimeout(() => {
            setShowConfetti(false);
        }, 2200);
    }, [checklist]);

    useEffect(() => {
        if (
            !checklist ||
            checklist.hidden ||
            !checklist.completed ||
            hasRequestedCompletionRef.current
        ) {
            return;
        }

        hasRequestedCompletionRef.current = true;
        completionTimeoutRef.current = setTimeout(() => {
            markCompleted().catch(() => {
                hasRequestedCompletionRef.current = false;
            });
        }, 1600);

        return () => {
            if (completionTimeoutRef.current) {
                clearTimeout(completionTimeoutRef.current);
            }
        };
    }, [checklist, markCompleted]);

    useEffect(() => {
        return () => {
            if (confettiTimeoutRef.current) {
                clearTimeout(confettiTimeoutRef.current);
            }
            if (completionTimeoutRef.current) {
                clearTimeout(completionTimeoutRef.current);
            }
        };
    }, []);

    if (isLoading || isError || !checklist || checklist.hidden) return null;

    const celebratedStepConfig = celebratedStepKey ? STEP_CONFIG[celebratedStepKey] : null;

    return (
        <aside
            className={cn(
                'fixed bottom-4 z-50 sm:bottom-6 sm:right-6',
                isCollapsed
                    ? 'inset-x-4 sm:left-auto sm:w-[16rem]'
                    : 'inset-x-4 sm:left-auto sm:w-[23rem]',
            )}
        >
            <section
                className={cn(
                    'relative overflow-hidden bg-card transition-all duration-300',
                    isCollapsed
                        ? 'rounded-2xl shadow-elevation-1'
                        : 'rounded-3xl border border-brand/15 shadow-2xl shadow-brand/10 ring-1 ring-brand/10',
                )}
            >
                {showConfetti ? (
                    <div className="pointer-events-none absolute right-0 top-0 z-20 size-32 -translate-y-9 translate-x-7 sm:size-40">
                        <ConcludeAnimation />
                    </div>
                ) : null}

                <div
                    className={cn(
                        'relative z-10 flex flex-col',
                        isCollapsed
                            ? 'gap-2 bg-card px-4 py-3 text-foreground'
                            : 'gap-3 bg-brand p-4 text-primary-foreground',
                    )}
                >
                    <div className="flex items-center gap-3">
                        <div className="min-w-0 flex-1">
                            <h2
                                className={cn(
                                    'font-semibold tracking-[-0.01em]',
                                    isCollapsed
                                        ? 'text-sm leading-5 text-foreground'
                                        : 'text-base leading-6',
                                )}
                            >
                                {isCollapsed
                                    ? `Primeiros passos · ${checklist.progress.completed}/${checklist.progress.total}`
                                    : 'Você está indo bem.'}
                            </h2>
                            {!isCollapsed ? (
                                <p className="mt-1 text-xs leading-5 text-primary-foreground/65">
                                    Complete essas etapas para chegar ao primeiro laudo finalizado.
                                </p>
                            ) : null}
                        </div>

                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => setIsCollapsed((current) => !current)}
                            className={cn(
                                'size-8 flex-shrink-0 rounded-full',
                                isCollapsed
                                    ? 'text-brand/70 hover:bg-brand/10 hover:text-brand'
                                    : 'text-primary-foreground/70 hover:bg-primary-foreground/10 hover:text-primary-foreground',
                            )}
                            aria-label={isCollapsed ? 'Expandir checklist' : 'Minimizar checklist'}
                        >
                            {isCollapsed ? (
                                <ArrowUp2 className="size-4" />
                            ) : (
                                <ArrowDown2 className="size-4" />
                            )}
                        </Button>
                    </div>

                    <div className="flex items-center gap-3">
                        <Progress
                            value={checklist.progress.percent}
                            className={cn(
                                isCollapsed ? 'h-1 bg-brand/10' : 'h-2 bg-primary-foreground/20',
                            )}
                            indicatorClassName={cn(
                                isCollapsed ? 'bg-brand' : 'bg-primary-foreground',
                            )}
                        />
                        {!isCollapsed ? (
                            <span className="w-10 text-right text-xs font-semibold text-primary-foreground/70">
                                {checklist.progress.completed}/{checklist.progress.total}
                            </span>
                        ) : null}
                    </div>
                </div>

                {!isCollapsed ? (
                    <div className="relative z-10 grid max-h-[min(28rem,calc(100svh-20rem))] gap-2 overflow-y-auto bg-card p-3">
                        {showConfetti && celebratedStepConfig ? (
                            <div className="rounded-2xl border border-success/20 bg-success-subtle px-3 py-2 text-xs font-medium text-success">
                                Etapa concluída: {celebratedStepConfig.title}
                            </div>
                        ) : null}

                        {checklist.steps.map((step) => {
                            const config = STEP_CONFIG[step.key];
                            const Icon = config.icon;
                            const shouldShowStepAction =
                                !step.completed && Boolean(config.actionPath);

                            return (
                                <div
                                    key={step.key}
                                    className={cn(
                                        'group flex items-center gap-3 rounded-2xl border bg-card p-3 transition-all',
                                        step.completed
                                            ? 'border-success/20 shadow-sm'
                                            : 'border-border hover:border-brand/25 hover:shadow-sm',
                                    )}
                                >
                                    <div
                                        className={cn(
                                            'flex size-9 flex-shrink-0 items-center justify-center rounded-xl',
                                            step.completed
                                                ? 'bg-success-subtle text-success'
                                                : 'bg-brand-subtle text-brand',
                                        )}
                                    >
                                        <Icon className="size-4" variant="Bulk" />
                                    </div>

                                    <div className="min-w-0 flex-1">
                                        <h3
                                            className={cn(
                                                'text-sm font-semibold leading-5',
                                                step.completed
                                                    ? 'text-muted-foreground line-through decoration-border-strong'
                                                    : 'text-foreground',
                                            )}
                                        >
                                            {config.title}
                                        </h3>
                                        <p className="mt-0.5 line-clamp-2 text-xs leading-5 text-muted-foreground">
                                            {config.description}
                                        </p>
                                    </div>

                                    <div className="flex size-8 flex-shrink-0 items-center justify-center">
                                        {step.completed ? (
                                            <TickCircle className="size-6 text-success" />
                                        ) : shouldShowStepAction && config.actionPath ? (
                                            <Button
                                                asChild
                                                variant="surface"
                                                size="icon"
                                                className="size-8 rounded-full text-brand hover:bg-brand-subtle hover:text-brand"
                                            >
                                                <Link
                                                    to={config.actionPath}
                                                    onClick={() =>
                                                        capture('readiness_step_cta_clicked', {
                                                            step_key: step.key,
                                                        })
                                                    }
                                                    aria-label={`Ir para ${config.actionLabel ?? config.title}`}
                                                >
                                                    <ArrowRight className="size-3.5" />
                                                </Link>
                                            </Button>
                                        ) : null}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : null}
            </section>
        </aside>
    );
}
